import { createStore } from 'vuex'

export default createStore({
  state: {
    user: null, // Holds all user related data
    platformExchanges: null, // Holds all exchanges we support
    exchangeTradingPairs: {}, // Holds all trading pairs per exchangeId
    getUserLogsPage: 1, // Init
    getUserLogsLimit: 10, // Init
    theme: 'light', // Init
    protectPrivacy: false // Init
  },
  getters: {
    isUserLoggedIn (state) {
      return (state.user?.profile?.access_token)? true : false
    },
    getUser (state) {
      return state.user
    },
    getUserProfile (state) {
      return state.user?.profile
    },
    getUserStripePortalSession (state) {
      return state.user?.stripeSession
    },
    getUserBots (state) {
      return state.user?.bots
    },
    getUserBotAssets (state) {
      return state.user?.botAssets
    },
    getUserBotErrors (state) {
      return state.user?.botErrors
    },
    getUserDashboardAssets (state) {
      return state.user?.dashboard?.assets
    },
    getUserDashboardBotAssets (state) {
      return state.user?.dashboard?.botAssets
    },
    getUserDashboardBotPositions (state) {
      return state.user?.dashboard?.botPositions
    },
    getUserDashboardUsdPerExchange (state) {
      return state.user?.dashboard?.usdPerExchange
    },
    getUserDashboardUsdPerBotCategory (state) {
      return state.user?.dashboard?.usdPerBotCategory
    },
    getUserLogs (state) {
      return state.user?.logs
    },
    getUserLogsPageLimit(state) {
      return { page: state.getUserLogsPage, limit: state.getUserLogsLimit }
    },
    getUserBotLogs: (state) => (botId) => {
      if (state.user?.botLogs && state.user.botLogs[botId]) {
        return state.user.botLogs[botId]
      }
      return []
    },
    getPlatformExchanges (state) {
      return state.platformExchanges
    },
    getUTMs (state) {
      return state.user?.utms
    },
    getTheme (state) {
      return state.theme
    },
    getProtectPrivacy (state) {
      return state.protectPrivacy
    }
  },
  actions: {
    getPlatformExchanges: async ({ commit, getters }) => {
      console.log('getPlatformExchanges')

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/exchanges'
      const rawResponse = await fetch(url, {
        method: "GET",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Accept': 'application/json'
        }
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects
        commit('setPlatformExchanges', { exchanges: response })
      } else {
        // TODO: handle user token is now invalid for example and other states
      }
    },
    getExchangeTradingPairs: async ({ commit, getters }, { exchangeId, tradingAccount, botId }) => {
      console.log('getExchangeTradingPairs')

      if (!exchangeId || !botId) return null

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/exchange/pairs/'+exchangeId+'?botId='+botId
      const rawResponse = await fetch(url, {
        method: "GET",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Accept': 'application/json'
        }
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects

        // Remove all spaces and slashes
        const tradingPairs = response.map(pair => {
          pair.symbol = pair.symbol.replace('/', '').replace(' ', '')
          return pair
        });

        commit('setExchangeTradingPairs', { exchangeId: exchangeId, tradingAccount: tradingAccount, exchangeTradingPairs: tradingPairs })

        // Unset all bot errors
        // TODO: Cannot do it here because it would remove other API call errors if this API call works. We must show all errors until the user reloads the bot page or similar
        // commit('setUserBotError', { botId: botId, errorResponseObj: null})
      } else {
        const response = await rawResponse.json()

        // Store the error this bot produced so we can show it to the user when necessary
        commit('setUserBotError', { botId: botId, errorResponseObj: response})
      }
    },
    getExchangeTradingPairsByApiKeys: async ({ commit, getters }, { exchangeId, tradingAccount, apiKey, apiSecret, apiPassphrase }) => {
      console.log('getExchangeTradingPairs')

      // Do not validate the apiPassphrase because not all exchanges need it
      if (!exchangeId || !tradingAccount || !apiKey || !apiSecret) return null

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      let postData = {
        apiKey: apiKey,
        secretKey: apiSecret.replace(/\\n/g, "\n"), // Fixing the JSON.stringify issue for Coinbase
        passphrase: apiPassphrase,
        publicAddress: apiPassphrase,
        tradingAccount: tradingAccount,
        isTestNet: false
      }

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/exchange/pairs/'+exchangeId
      const rawResponse = await fetch(url, {
        method: "POST",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData), // body data type must match "Content-Type" header
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects

        // Remove all spaces and slashes
        const tradingPairs = response.map(pair => {
          pair.symbol = pair.symbol.replace('/', '')
          pair.symbol = pair.symbol.replace(' ', '')
          return pair
        });

        commit('setExchangeTradingPairs', { exchangeId: exchangeId, tradingAccount: tradingAccount, exchangeTradingPairs: tradingPairs })
      } else {
        // TODO: handle user token is now invalid for example and other states
      }
    },
    getBotAssets: async ({ commit, getters }, { exchangeId, botId }) => {
      // Check if enough time passed to justify fetching it again
      const botAssets = getters.getUserBotAssets
      if (botAssets && botAssets[botId]) {

        const updatedAt = botAssets[botId].updatedAt
        const timeNow = Math.floor(new Date().getTime() / 1000)

        // No need to update botAssets again within 10 seconds
        if (timeNow -10 <= updatedAt) return true
      }

      console.log('getBotAssets', botId)

      if (!exchangeId || !botId) return null

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/exchange/assets/'+exchangeId+'?botId='+botId
      const rawResponse = await fetch(url, {
        method: "GET",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Accept': 'application/json'
        }
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects

        // Store assets
        commit('setUserBotAssets', { botId: botId, assets: response })

        // Unset all bot errors
        // TODO: Cannot do it here because it would remove other API call errors if this API call works. We must show all errors until the user reloads the bot page or similar
        // commit('setUserBotError', { botId: botId, errorResponseObj: null})
      } else {
        // Store assets
        commit('setUserBotAssets', { botId: botId, assets: {} })

        const response = await rawResponse.json()
        // Store the error this bot produced so we can show it to the user when necessary
        commit('setUserBotError', { botId: botId, errorResponseObj: response})
      }
    },
    getBotAssetsByApiKeys: async ({ commit, getters }, { exchangeId, exchangeTradingAccount, apiKey, apiSecret, apiPassphrase }) => {
      console.log('getBotAssetsByApiKeys')

      // Do not validate the apiPassphrase because not all exchanges need it
      if (!exchangeId || !exchangeTradingAccount || !apiKey || !apiSecret) return null

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      let postData = {
        apiKey: apiKey,
        secretKey: apiSecret.replace(/\\n/g, "\n"), // Fixing the JSON.stringify issue for Coinbase
        passphrase: apiPassphrase,
        publicAddress: apiPassphrase,
        tradingAccount: exchangeTradingAccount,
        isTestNet: false
      }

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/exchange/assets/'+exchangeId
      const rawResponse = await fetch(url, {
        method: "POST",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData), // body data type must match "Content-Type" header
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects

        // Store assets - 0 is used as a temporary ID until the Bot get's an ID from the API
        commit('setUserBotAssets', { botId: 0, assets: response })

      } else {
        // Store assets - 0 is used as a temporary ID until the Bot get's an ID from the API
        commit('setUserBotAssets', { botId: 0, assets: {} })
      }
    },
    getUserBots: async ({ commit, getters }) => {
      console.log('getUserBots')

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/bots'
      const rawResponse = await fetch(url, {
        method: "GET",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Accept': 'application/json'
        }
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects
        commit('setUserBots', { userBots: response })
      } else {
        // TODO: handle user token is now invalid for example and other states
      }
    },
    getUserLogs: async ({ commit, getters }, {page, limit}) => {
      console.log('getUserLogs')

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      // Fetch page and limit from the store
      const userLogsPageLimit = getters.getUserLogsPageLimit
      if (!page) page = userLogsPageLimit.page
      if (!limit) limit = userLogsPageLimit.limit

      // Make API call
      const url = process.env.VUE_APP_API_URL+'/auditlog?page='+page+'&limit='+limit
      const rawResponse = await fetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Accept': 'application/json'
        }
      })

      // Check status codes, handle errors and process response
      if (rawResponse.status === 200) {
        const response = await rawResponse.json()
        commit('setUserLogs', { logs: response })
      } else {
        // TODO: Handle errors
      }
    },
    getUserBotLogs: async ({ commit, getters }, { botId, page, limit }) => {
      console.log('getUserBotLogs')

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      if (!page) page = 1
      if (!limit) limit = 100

      // Make API call
      const url = process.env.VUE_APP_API_URL+'/auditlog/bot/'+botId+'?page='+page+'&limit='+limit
      const rawResponse = await fetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Accept': 'application/json'
        }
      })

      // Check status codes, handle errors and process response
      if (rawResponse.status === 200) {
        const response = await rawResponse.json()
        commit('setUserBotLogs', { botId: botId, logs: response })

        // Unset all bot errors
        // TODO: Cannot do it here because it would remove other API call errors if this API call works. We must show all errors until the user reloads the bot page or similar
        // commit('setUserBotError', { botId: botId, errorResponseObj: null})
      } else {
        const response = await rawResponse.json()
        
        // Store the error this bot produced so we can show it to the user when necessary
        commit('setUserBotError', { botId: botId, errorResponseObj: response})
      }
    },
    createBot: async ({ dispatch, getters }, bot) => {
      console.log('createBot', bot)

      if (!bot) return null

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      const postData = bot

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/bots'
      const rawResponse = await fetch(url, {
        method: "POST",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData), // body data type must match "Content-Type" header
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects

        // TODO: it's better to fetch only that 1 bot and add it to the state manually
        // now fetch all user bots fresh
        await dispatch('getUserBots')

        return response

      } else {
        // TODO: handle user token is now invalid for example and other states
      }

      return {}
    },
    updateBot: async ({ dispatch, getters }, bot) => {
      // console.log('updateBot', bot)

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      const postData = bot

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/bots/' + bot.id
      const rawResponse = await fetch(url, {
        method: "PATCH",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData), // body data type must match "Content-Type" header
      })

      if (rawResponse.status === 200) {
        // const response = await rawResponse.json() // parses JSON response into native JavaScript objects

        // Unset all bot errors
        // TODO: We cannot do this here. It can only be reset when we allow updating the API Keys!
        // commit('setUserBotError', { botId: bot.id, errorResponseObj: null})

        // TODO: It's better to fetch only that 1 bot and add it to the state manually
        // Fetch all user bots again
        await dispatch('getUserBots')

      } else {
        // TODO: Handle user token is now invalid for example and other states
      }
    },
    deleteBot: async ({ dispatch, commit, getters }, bot) => {
      // console.log('deleteBot', bot)

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/bots/' + bot.id
      const rawResponse = await fetch(url, {
        method: "DELETE",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })

      if (rawResponse.status === 200) {
        // Unset all bot errors
        commit('setUserBotError', { botId: bot.id, errorResponseObj: null})

        // TODO: it's better to fetch only that 1 bot and add it to the state manually
        // now fetch all user bots fresh
        await dispatch('getUserBots')

        // TODO: Loading page = 1 and limit = 10 here is basically overruling whatever the user had loaded already in the LogsList component. Those value are not in sync anymore. Not the best.
        // TODO: Maybe it's better to have getUserLogsPage and getUserLogsLimit in the store and use those accross the whole app.
        const userLogsPageLimit = getters.getUserLogsPageLimit
        await dispatch('getUserLogs', {page: userLogsPageLimit.page, limit: userLogsPageLimit.limit})

      } else {
        // TODO: handle user token is now invalid for example and other states
      }
    },
    getAllUserAssets: async ({ dispatch, commit, getters }) => {
      console.log('getAllUserAssets')

      //*** Define vars and do initial checks ***//
      // Fetch userBots if not yet there
      if (!getters.getUserBots) await dispatch('getUserBots')

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      // If the user has no bots, then set the user assets to an empty []
      if (!getters.getUserBots || getters.getUserBots.length <= 0) {
        commit('setUserDashboardAssets', { allUserAssets: [] })
        commit('setUserDashboardBotAssets', { userBotAssets: [] })
        commit('setUserDashboardBotPositions', { userBotPositions: [] })
        commit('setUserDashboardUsdPerExchange', { userUsdPerExchange: [] })
        return false
      }

      let allUserBotAssets = [] // Assets per bot - adding the USD value to it below
      let allUserBotPositions = [] // Positions per bot - adding the USD value to it below
      let allUserAssetsPerApiKey = [] // Assets per apiKey
      let allUserAssetsPerExchange = [] // Assets per exchange id
      let allUserAssetsPerBotCategory = [] // Assets per bot category
      let allUserAssets = [] // Final result containing the USD values

      //*** Fetch all balances and positions from all bots in parallel batches ***//
      const batchSize = 10 // Setting batchSize to a high number can cause the browser to reject some requests so we can't simply use getters.getUserBots.length here
      const bots = getters.getUserBots
      const allFetchedData = [] // Store all fetched data here
      
      // Fetch all data in batches FIRST
      for (let i = 0; i < bots.length; i += batchSize) {
        const batch = bots.slice(i, i + batchSize)
        
        // Create promises for current batch
        const batchPromises = batch.map(async (bot) => {
          const url = process.env.VUE_APP_API_URL+'/exchange/assets/'+bot.exchangeId+'?botId='+bot.id

          try {
            const rawResponse = await fetch(url, {
              method: "GET",
              mode: "cors",
              cache: "no-cache",
              headers: {
                'Authorization': 'Bearer '+userProfile.access_token,
                'Accept': 'application/json'
              }
            })

            // Update UI to show which bots are being fetched
            commit('setDashboardNowFetchingBot', { bot: bot })
            
            if (rawResponse.status === 200) {
              const response = await rawResponse.json()
              return { bot, response, success: true }
            } else {
              const errorResponse = await rawResponse.json()
              return { bot, response: null, success: false, error: errorResponse }
            }
          } catch (error) {
            console.error(`Error fetching data for bot ${bot.id}:`, error)
            return { bot, response: null, success: false, error: error }
          }
        })
        
        // Wait for current batch to complete and store results
        const batchResults = await Promise.all(batchPromises)
        allFetchedData.push(...batchResults)
      }
      
      //*** Now process ALL the fetched data ***//
      for (const result of allFetchedData) {
        const bot = result.bot
        
        if (result.success && result.response) {
          // Store assets
          commit('setUserBotAssets', { botId: bot.id, assets: result.response })
          
          // Iterate over the balances (aka. assets)
          // De-duplicate when apiKey/exchangeAccount is used multiple times
          for (let asset of result.response['balance']) {
            const apiKey = bot.exchangeConfig.apiKey
            const uniqueKey = apiKey+'_'+asset.coin

            // Assets per api key (to de-duplicate when the same API key is used twice)
            // TODO: Also try to filter out cases where the same account is connected but via a different API key
            allUserAssetsPerApiKey[uniqueKey] = {
              coin: asset.coin,
              balance: asset.balance
            }

            // Assets per bot
            allUserBotAssets.push({
              botId: bot.id,
              coin: asset.coin,
              balance: asset.balance
            })

            // Assets per exchange
            if (!allUserAssetsPerExchange[bot.exchangeId]) allUserAssetsPerExchange[bot.exchangeId] = []
            allUserAssetsPerExchange[bot.exchangeId].push({
              exchangeId: bot.exchangeId,
              coin: asset.coin,
              balance: asset.balance
            })

            // Assets per bot category
            let botCategory = (!bot.category || bot.category == '') ? null : bot.category // Unify empty and null categories into one item
            if (!allUserAssetsPerBotCategory[botCategory]) allUserAssetsPerBotCategory[botCategory] = []
            allUserAssetsPerBotCategory[botCategory].push({
              botCategory: botCategory,
              coin: asset.coin,
              balance: asset.balance
            })
          }

          // Iterate over all positions
          for (let position of result.response['positions']) {
            const apiKey = bot.exchangeConfig.apiKey
            const uniqueKey = apiKey+'_'+position.baseCoin

            // Copied this code from the assets loop above just to add those positions to that dataset as well
            // Assets per api key (to de-duplicate when the same API key is used twice)
            // TODO: Also try to filter out cases where the same account is connected but via a different API key
            allUserAssetsPerApiKey[uniqueKey] = {
              coin: position.baseCoin,
              balance: position.sizeAsBaseCoin / position.leverage // TODO: when we need to add the P&L then simply do + (position.sizeasBaseCoin * position.unrealizedPnlAsPercentage)
            }

            // Assets per bot
            // We shall not mingle Positions into the assets per bot data

            // Assets per exchange
            if (!allUserAssetsPerExchange[bot.exchangeId]) allUserAssetsPerExchange[bot.exchangeId] = []
            allUserAssetsPerExchange[bot.exchangeId].push({
              exchangeId: bot.exchangeId,
              coin: position.baseCoin,
              balance: position.sizeAsBaseCoin / position.leverage // TODO: when we need to add the P&L then simply do + (position.sizeasBaseCoin * position.unrealizedPnlAsPercentage)
            })

            // Assets per bot category
            let botCategory = (!bot.category || bot.category == '') ? null : bot.category // Unify empty and null categories into one item
            if (!allUserAssetsPerBotCategory[botCategory]) allUserAssetsPerBotCategory[botCategory] = []
            allUserAssetsPerBotCategory[botCategory].push({
              botCategory: botCategory,
              coin: position.baseCoin,
              balance: position.sizeAsBaseCoin / position.leverage // TODO: when we need to add the P&L then simply do + (position.sizeasBaseCoin * position.unrealizedPnlAsPercentage)
            })

            // Positions per bot
            allUserBotPositions.push({
              ...position,
              botId: bot.id
            })
          }

          // Unset all bot errors
          // TODO: Cannot do it here because it would remove other API call errors if this API call works. We must show all errors until the user reloads the bot page or similar.
          // commit('setUserBotError', { botId: bot.id, errorResponseObj: null})
          
        } else {
          // Store empty assets for failed requests
          commit('setUserBotAssets', { botId: bot.id, assets: {} })
          
          // Store the error this bot produced so we can show it to the user when necessary
          commit('setUserBotError', { botId: bot.id, errorResponseObj: result.error })
        }
      }

      // Cannot check if allUserAssetsPerExchangeAccount is empty or not because it's an associative array (shrug)

      // Group per asset. Results in 2D Object with all occurrences of 1 asset inside the same index
      const allUserAssetsGrouped = Object.groupBy(Object.values(allUserAssetsPerApiKey), ({ coin }) => coin);

      // Sum up the balances to get the total balance per asset
      for (const index in allUserAssetsGrouped) {
        allUserAssets[index] = {
          coin: index,
          balance: Number(0)
        }

        for (const index2 in allUserAssetsGrouped[index]) {
          const coin = allUserAssetsGrouped[index][index2]
          allUserAssets[index].balance += Number(coin.balance)
        }
      }

      //*** Define FIAT currencies ***//
      // https://github.com/datasets/currency-codes/blob/main/data/codes-all.csv
      const fiatCurrencies = ["AFN","EUR","ALL","DZD","USD","AOA","XCD","ARS","AMD","AWG","AUD","AZN","BSD","BHD","BDT","BBD","BYN","BZD","XOF","BMD","INR","BTN","BOV","BAM","BWP","NOK","BRL","BND","BGN","BIF","CVE","KHR","XAF","CAD","KYD","CLP","CLF","CNY","COP","COU","KMF","CDF","NZD","CRC","CUP","ANG","CZK","DKK","DJF","DOP","EGP","SVC","ERN","SZL","ETB","FKP","FJD","XPF","GMD","GEL","GHS","GIP","GTQ","GBP","GNF","GYD","HTG","HNL","HKD","HUF","ISK","IDR","XDR","IRR","IQD","ILS","JMD","JPY","JOD","KZT","KES","KPW","KRW","KWD","KGS","LAK","LBP","LSL","ZAR","LRD","LYD","CHF","MOP","MKD","MGA","MWK","MYR","MVR","MRU","MUR","XUA","MXN","MXV","MDL","MAD","MZN","MMK","NAD","NPR","NIO","NGN","OMR","PKR","PAB","PGK","PYG","PEN","PHP","PLN","QAR","RON","RUB","RWF","SHP","WST","STN","SAR","RSD","SCR","SLE","SGD","XSU","SBD","SOS","SSP","LKR","SDG","SRD","SEK","CHE","CHW","SYP","TWD","TJS","TZS","THB","TOP","TTD","TND","TRY","TMT","UGX","UAH","AED","USN","UYU","UYI","UYW","UZS","VUV","VES","VED","VND","YER","ZMW","ZWG","XBA","XBB","XBC","XBD","XTS","XXX","XAU","XPD","XPT","XAG","AFA","FIM","ALK","ADP","ESP","FRF","AOK","AON","AOR","ARA","ARP","ARY","RUR","ATS","AYM","AZM","BYB","BYR","BEC","BEF","BEL","BOP","BAD","BRB","BRC","BRE","BRN","BRR","BGJ","BGK","BGL","BUK","HRD","HRK","CUC","CYP","CSJ","CSK","ECS","ECV","GQE","EEK","XEU","GEK","DDM","DEM","GHC","GHP","GRD","GNE","GNS","GWE","GWP","ITL","ISJ","IEP","ILP","ILR","LAJ","LVL","LVR","LSM","ZAL","LTL","LTT","LUC","LUF","LUL","MGF","MVQ","MLF","MTL","MTP","MRO","MXP","MZE","MZM","NLG","NIC","PEH","PEI","PES","PLZ","PTE","ROK","ROL","STD","CSD","SLL","SKK","SIT","RHD","ESA","ESB","SDD","SDP","SRG","CHC","TJR","TPE","TRL","TMM","UGS","UGW","UAK","SUR","USS","UYN","UYP","VEB","VEF","VNC","YDD","YUD","YUM","YUN","ZRN","ZRZ","ZMK","ZWC","ZWD","ZWN","ZWR","ZWL","XFO","XRE","XFU"]

      //*** Create a comma separated list of all FIAT and CRYPTO assets ***//
      const userUniqueFiats = []
      const userUniqueCryptos = []
      for (const index in allUserAssets) {
        const coin = allUserAssets[index].coin

        if (fiatCurrencies.includes(coin)) {
          userUniqueFiats.push(coin)
        } else {
          // Ignore these because they are not useful and also they break the CoinMarketCap API (500 Server Error)
          if (coin != 'CRO.STAKED') {
            userUniqueCryptos.push(coin)
          }
        }
      }
      
      // Iterate over the Positions and add any missing crypto asset to the list of crypto assets to fetch USD prices for later on
      for(const index in allUserBotPositions) {
        const position = allUserBotPositions[index]
        
        // Add a missing crypto coin
        if (!userUniqueCryptos.includes(position.quoteCoin)) {
          userUniqueCryptos.push(position.quoteCoin)
        }
      }

      // console.log(userUniqueFiats)
      // console.log('userUniqueCryptos', userUniqueCryptos)

      // Convert asset lists to CSVs
      const userUniqueFiatsCsv = userUniqueFiats.join(',')
      const userUniqueCryptosCsv = userUniqueCryptos.join(',')

      // Check if the lists are not empty
      if (!userUniqueFiatsCsv && !userUniqueCryptosCsv) {
        console.log('No assets to load prices for.')
        commit('setUserDashboardAssets', { allUserAssets: [] })
        commit('setUserDashboardBotAssets', { userBotAssets: [] })
        commit('setUserDashboardBotPositions', { userBotPositions: [] })
        commit('setUserDashboardUsdPerExchange', { userUsdPerExchange: [] })
        return false
      }

      //*** Fetch FIAT USD prices ***//
      // TODO: Change the API we use to fetch those prices!!! The one from cryptocompare has a cost once we launch paid plans for SIGNUM!
      let fiatUsdPrices = null // Null so that later on we can check if this has been set or not
      if (userUniqueFiatsCsv) {
        const fiatApiUrl = 'https://min-api.cryptocompare.com/data/pricemulti?fsyms='+userUniqueFiatsCsv+'&tsyms=USD' // https://min-api.cryptocompare.com/documentation?key=Price&cat=multipleSymbolsPriceEndpoint
        const fiatRawResponse = await fetch(fiatApiUrl, {
          method: "GET",
          mode: "cors",
          cache: "no-cache",
        })
        if (fiatRawResponse.status === 200) {
          fiatUsdPrices = await fiatRawResponse.json()
        } else {
          console.log("Could not fetch fiat USD prices from the prices API.")
        }
      }

      //*** Fetch CRYPTO USD prices ***//
      let cryptoUsdPrices = null // Null so that later on we can check if this has been set or not
      if (userUniqueCryptosCsv) {
        const cryptoApiUrl = process.env.VUE_APP_API_URL+'/prices?fsyms='+userUniqueCryptosCsv+'&tsyms=USD'
        const cryptoRawResponse = await fetch(cryptoApiUrl, {
          method: "GET",
          mode: "cors",
          cache: "no-cache",
          headers: {
            'Authorization': 'Bearer '+userProfile.access_token,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        })
        if (cryptoRawResponse.status === 200) {
          cryptoUsdPrices = await cryptoRawResponse.json()
        } else {
          console.log("Could not fetch crypto USD prices from the prices API.")
        }
      }

      // Merge fiat and crypto price objects and continue normally
      // console.log(fiatUsdPrices)
      // console.log(cryptoUsdPrices)
      const assetsUsdPrices = Object.assign({}, fiatUsdPrices, cryptoUsdPrices)
      // console.log(assetsUsdPrices)

      if (!assetsUsdPrices) {
        console.log("Prices api did not return prices for given user assets.")
        commit('setUserDashboardAssets', { allUserAssets: [] })
        commit('setUserDashboardBotAssets', { userBotAssets: [] })
        commit('setUserDashboardBotPositions', { userBotPositions: [] })
        commit('setUserDashboardUsdPerExchange', { userUsdPerExchange: [] })
        return false
      }

      //*** Handle bot assets - add USD value for each coin ***//
      for (const assetIndex in allUserBotAssets) {
        const asset = allUserBotAssets[assetIndex]
        let coin = asset.coin
        const balance = Number(asset.balance)

        if (assetsUsdPrices[coin]) {
          const assetPriceInUsd = Number(assetsUsdPrices[coin].USD)

          allUserBotAssets[assetIndex].balanceUSD = assetPriceInUsd * balance
        } else {
          console.log("Bot assets: Did not find USD price for "+coin)
        }
      }

      // Transform to an array of objects
      // allUserBotAssets = Object.values(allUserBotAssets)

      // Sort data
      // console.log("allUserBotAssets", allUserBotAssets)
      allUserBotAssets.sort((a,b) => (a.botId < b.botId) ? 1 : ((b.botId < a.botId) ? -1 : 0))

      // Set data
      commit('setUserDashboardBotAssets', { userBotAssets: allUserBotAssets })

      //*** Handle bot positions ***//
      for (const index in allUserBotPositions) {
        const position = allUserBotPositions[index]
        
        if (assetsUsdPrices[position.quoteCoin]) {
          const assetPriceInUsd = Number(assetsUsdPrices[position.quoteCoin].USD)

          allUserBotPositions[index].unrealizedPnlAsQuoteCoinUSD = assetPriceInUsd * Number(allUserBotPositions[index].unrealizedPnlAsQuoteCoin)
          allUserBotPositions[index].currentCollateralAsQuoteCoinUSD = assetPriceInUsd * Number(allUserBotPositions[index].currentCollateralAsQuoteCoin)
        } else {
          console.log("Bot position: Did not find USD price for "+position.quoteCoin)
        }
      }

      // Sort data
      allUserBotPositions.sort((a,b) => (a.botId < b.botId) ? 1 : ((b.botId < a.botId) ? -1 : 0))

      // Set data
      commit('setUserDashboardBotPositions', { userBotPositions: allUserBotPositions })

      //*** Handle user assets - add USD value for each coin ***//
      for (const index in allUserAssets) {
        let coin = allUserAssets[index].coin
        const balance = Number(allUserAssets[index].balance)

        if (assetsUsdPrices[coin]) {
          const assetPriceInUsd = Number(assetsUsdPrices[coin].USD)
          allUserAssets[index].balanceUSD = assetPriceInUsd * balance
        } else {
          console.log("User assets: Did not find USD price for "+coin)
        }
      }

      // Transform to an array of objects
      allUserAssets = Object.values(allUserAssets)

      // Sort data
      allUserAssets.sort((a,b) => (a.balanceUSD < b.balanceUSD) ? 1 : ((b.balanceUSD < a.balanceUSD) ? -1 : 0))

      // Set data
      commit('setUserDashboardAssets', { allUserAssets: allUserAssets })

      //*** Handle exchange assets - add and sum up the USD value per exchange ***//
      let userUsdPerExchange = []
      // Add USD values to all assets which are grouped per exchange
      for (const exchangeId in allUserAssetsPerExchange) {
        let totalUSD = 0

        for (const assetIndex in allUserAssetsPerExchange[exchangeId]) {
          const asset = allUserAssetsPerExchange[exchangeId][assetIndex]
          let coin = asset.coin
          const balance = Number(asset.balance)

          if (assetsUsdPrices[coin]) {
            const assetPriceInUsd = Number(assetsUsdPrices[coin].USD)

            // Sum up the USD value per exchange
            totalUSD += assetPriceInUsd * balance

          } else {
            console.log("Exchange allocations: Did not find USD price for "+coin)
          }
        }
        userUsdPerExchange.push({
          exchangeId: exchangeId,
          balanceUSD: totalUSD
        })
      }

      // Sort data
      userUsdPerExchange.sort((a,b) => (a.balanceUSD < b.balanceUSD) ? 1 : ((b.balanceUSD < a.balanceUSD) ? -1 : 0))

      // Set data
      commit('setUserDashboardUsdPerExchange', { userUsdPerExchange: userUsdPerExchange })

      //*** Handle bot category assets - add and sum up the USD value per bot category ***//
      let userUsdPerBotCategory = []
      // Add USD values to all assets, which are grouped per bot category
      for (const botCategory in allUserAssetsPerBotCategory) {
        let totalUSD = 0

        for (const assetIndex in allUserAssetsPerBotCategory[botCategory]) {
          const asset = allUserAssetsPerBotCategory[botCategory][assetIndex]
          let coin = asset.coin
          const balance = Number(asset.balance)

          if (assetsUsdPrices[coin]) {
            const assetPriceInUsd = Number(assetsUsdPrices[coin].USD)

            // Sum up the USD value per exchange
            totalUSD += assetPriceInUsd * balance

          } else {
            console.log("Bot category allocations: Did not find USD price for "+coin)
          }
        }
        userUsdPerBotCategory.push({
          botCategory: botCategory,
          balanceUSD: totalUSD
        })
      }

      // Sort data
      userUsdPerBotCategory.sort((a,b) => (a.balanceUSD < b.balanceUSD) ? 1 : ((b.balanceUSD < a.balanceUSD) ? -1 : 0))

      // Set data
      commit('setUserDashboardUsdPerBotCategory', { userUsdPerBotCategory: userUsdPerBotCategory })
    },
    getUserProfile: async ({ commit, getters }) => {
      console.log('fetchUserProfile')

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      // Make API call
      const url = process.env.VUE_APP_API_URL+'/users/me'
      const rawResponse = await fetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Accept': 'application/json'
        }
      })

      // Check status codes, handle errors and process response
      if (rawResponse.status === 200) {
        const response = await rawResponse.json()

        // For backwards compatibilty
        response.access_token = response.accessTokenCurrent

        commit('setUserProfile', { userProfile: response })
      } else {
        // TODO: Handle errors
      }
    },
    getUserStripePortalSession: async ({ commit, getters }) => {
      console.log('getUserStripePortalSession')

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      // Don't fetch it again if it's still valid
      const timeSecondsNow = Math.floor(Date.now() / 1000)
      const stripeSession = getters.getUserStripePortalSession
      if (stripeSession && stripeSession.created + 60*10 >= timeSecondsNow) {
        return stripeSession
      }

      // Make API call
      const url = process.env.VUE_APP_API_URL+'/users/me/stripe/portal_session'
      const rawResponse = await fetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Accept': 'application/json'
        }
      })

      // Check status codes, handle errors and process response
      if (rawResponse.status === 200) {
        const response = await rawResponse.json()

        commit('setUserStripePortalSession', { userStripeSession: response })
        return response

      } else {
        // TODO: Handle errors
      }

      return null
    },
    getUserCannyToken: async ({ commit, getters }) => {
      console.log('getUserCannyToken')

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      // Make API call
      const url = process.env.VUE_APP_API_URL+'/users/me/canny_io/token'
      const rawResponse = await fetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Accept': 'application/json'
        }
      })

      // Check status codes, handle errors and process response
      if (rawResponse.status === 200) {
        const response = await rawResponse.json()

        commit('setUserCannyToken', { cannyTokenResponse: response })
        return response

      } else {
        // TODO: Handle errors
      }

      return null
    },
    addUserToStrategy: async ({ getters }, codes) => {
      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      let postData = {
        strategyCodes: codes
      }

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/strategies/add'
      const rawResponse = await fetch(url, {
        method: "PATCH",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData), // body data type must match "Content-Type" header
      })

      if (rawResponse.status === 200) {
        return true

      } else {
        // TODO: handle user token is now invalid for example and other states
      }

      return false
    },
    updateUserProfile: async ({ commit, getters }, newUserProfile) => {
      console.log('updateUserProfile')

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      const postData = newUserProfile

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/users/me'
      const rawResponse = await fetch(url, {
        method: "PATCH",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData), // body data type must match "Content-Type" header
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects

        await commit('setUserProfile', { userProfile: response})

        return true
      } else {
        // TODO: handle user token is now invalid for example and other states
      }

      return false
    },
    sendTradingSignal: async ({ getters }, { signal }) => {
      // console.log('sendTradingSignal')

      if (!getters.isUserLoggedIn) return null
      const userProfile = getters.getUserProfile

      const postData = signal

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/signals'
      const rawResponse = await fetch(url, {
        method: "POST",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Authorization': 'Bearer '+userProfile.access_token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData), // body data type must match "Content-Type" header
      })

      if (rawResponse.status === 200) {
        // const response = await rawResponse.json() // parses JSON response into native JavaScript objects
        return true
      } else {
        // TODO: handle user token is now invalid for example and other states
      }

      return false
    },
    logout: ({ commit }) => {
      commit('resetUser')
    }
  },
  mutations: {
    setPlatformExchanges (state, { exchanges }) {
      state.platformExchanges = exchanges
    },
    setExchangeTradingPairs (state, { exchangeId, tradingAccount, exchangeTradingPairs }) {
      if (!state.exchangeTradingPairs[exchangeId]) state.exchangeTradingPairs[exchangeId] = {}
      if (!state.exchangeTradingPairs[exchangeId][tradingAccount]) state.exchangeTradingPairs[exchangeId][tradingAccount] = []
      
      state.exchangeTradingPairs[exchangeId][tradingAccount] = exchangeTradingPairs
    },
    setUserProfile (state, { userProfile }) {
      if (!state.user) state.user = {}

      // For backwards compatibility until we get rid of the access_token field
      if (!userProfile.access_token || userProfile.access_token == '') {
        userProfile.access_token = userProfile.accessTokenCurrent
      }

      state.user.profile = userProfile
      localStorage.setItem('userProfile', JSON.stringify(userProfile))
    },
    setUserStripePortalSession (state, { userStripeSession }) {
      if (!state.user) state.user = {}

      state.user.stripeSession = userStripeSession
    },
    setUserCannyToken (state, { cannyTokenResponse }) {
      if (!state.user) state.user = {}

      state.user.canny = cannyTokenResponse
    },
    resetUser (state) {
      state.user = null
      localStorage.removeItem('userProfile')
      localStorage.removeItem('userUtms')
    },
    setUserBots (state, { userBots }) {
      state.user.bots = userBots
    },
    setUserBotAssets (state, { botId, assets }) {
      if (!state.user) state.user = {}
      if (!state.user.botAssets) state.user.botAssets = {}

      if (assets) {
        const updatedAt = Math.floor(new Date().getTime() / 1000)
        assets.updatedAt = updatedAt // Add the last updated date so we don't fetcht his too often from API
        state.user.botAssets[botId] = assets // Set it
      } else {
        delete state.user.botAssets[botId] // Unset it if it's null
      }
    },
    setUserBotError (state, { botId, errorResponseObj }) {
      if (!state.user) state.user = {}
      if (!state.user.botErrors) state.user.botErrors = {}

      if (errorResponseObj) state.user.botErrors[botId] = errorResponseObj // Set it
      else delete state.user.botErrors[botId] // Unset it if it's null
    },
    setDashboardNowFetchingBot (state, { bot }) {
      if (!state.user) state.user = {}
      if (!state.user.dashboard) state.user.dashboard = {}
      state.user.dashboard.nowFetchingBot = bot
    },
    setUserDashboardAssets (state, { allUserAssets }) {
      if (!state.user.dashboard) state.user.dashboard = {}
      state.user.dashboard.assets = allUserAssets
    },
    setUserDashboardBotAssets (state, { userBotAssets }) {
      if (!state.user.dashboard) state.user.dashboard = {}
      state.user.dashboard.botAssets = userBotAssets
    },
    setUserDashboardBotPositions (state, { userBotPositions }) {
      if (!state.user.dashboard) state.user.dashboard = {}
      state.user.dashboard.botPositions = userBotPositions
    },
    setUserDashboardUsdPerExchange (state, { userUsdPerExchange }) {
      if (!state.user.dashboard) state.user.dashboard = {}
      state.user.dashboard.usdPerExchange = userUsdPerExchange
    },
    setUserDashboardUsdPerBotCategory (state, { userUsdPerBotCategory }) {
      if (!state.user.dashboard) state.user.dashboard = {}
      state.user.dashboard.usdPerBotCategory = userUsdPerBotCategory
    },
    setUserBotLogs (state, { botId, logs }) {
      if (!state.user.botLogs) {
        state.user.botLogs = {}
      }
      state.user.botLogs[botId] = logs
    },
    setUserLogs (state, { logs }) {
      if (!state.user.logs) {
        state.user.logs = {}
      }
      state.user.logs = logs
    },
    setUserLogsPageLimit (state, { page, limit }) {
      state.getUserLogsPage = page
      state.getUserLogsLimit = limit
    },
    setUTMs (state, { utms }) {
      if (!state.user) state.user = {}

      // Set them fresh if they are not empty
      if (utms.medium || utms.source || utms.campaign || utms.term || utms.content) {
        state.user.utms = utms
        localStorage.setItem('userUtms', JSON.stringify(utms))
      } else { // Otherwise load them from local storage to always have them available
        utms = JSON.parse(localStorage.getItem("userUtms"));
        state.user.utms = utms
      }
    },
    setTheme (state, { theme }) {
      // Set fresh if it's not empty
      if (theme) {
        state.theme = theme
        localStorage.setItem('theme', theme)
      } else { // Otherwise fetch from local storage
        theme = localStorage.getItem('theme');

        // If theme was never set, set the default theme
        if (!theme || theme == '') {
          theme = 'light'
          localStorage.setItem('theme', theme)
        }

        // Store it in local
        state.theme = theme
      }
    },
    setProtectPrivacy (state, { privacy }) {
      // Set fresh if it's not empty
      if (privacy === true || privacy === false) {
        state.protectPrivacy = privacy
        localStorage.setItem('protectPrivacy', privacy)
      } else { // Otherwise fetch from local storage
        privacy = localStorage.getItem('protectPrivacy') == 'true' ? true : false

        // If theme was never set, set the default theme
        if (!privacy || privacy == '') {
          privacy = false
          localStorage.setItem('protectPrivacy', privacy)
        }

        // Store it in local
        state.protectPrivacy = privacy
      }
    }
  }
})
